import { generatePath } from 'react-router-dom';

import { RoutePath } from 'enums/Routes';
import { CheckingAccountResult, RepaymentDecisionResult } from 'enums/FlowNextResults';
import FinancialDashboard from 'components/FinancialCheckup/FinancialDashboard';
import ManageYourLoan from 'components/FinancialCheckup/FinancialDashboard/Loan/ManageYourLoan';
import TransactionDetail from 'components/FinancialCheckup/TransactionDetail/TransactionDetail';
import LoanPaymentDetail from 'components/FinancialCheckup/LoanPaymentDetail/LoanPaymentDetail';
import PaymentSettings from 'components/FinancialCheckup/PaymentSettings';
import UpdateEmployer from 'components/FinancialCheckup/UpdatePayments/UpdateEmployer';
import SetupDeduction from 'components/SetupDeduction';
import PaystubsSignInGuide from 'components/SetupDeduction/ManualDeduction/components/PaystubsSignInGuide';
import withFinancialDashboardNavigation from 'components/FinancialCheckup/withFinancialDashboardNavigation';
import SetupViaPayroll from 'components/SetupDeduction/ManualDeduction/components/SetupViaPayroll';
import WhatIsTheIssue from 'components/SetupDeduction/ManualDeduction/components/WhatIsTheIssue';
import NotifyYourTeam from 'components/SetupDeduction/ManualDeduction/components/NotifyYourTeam';
import VerifyPayrollDeduction from 'components/SetupDeduction/ManualDeduction/components/VerifyPayrollDeduction';
import UpdateAchConnect from 'components/FinancialCheckup/UpdatePayments/UpdateAchConnect';
import UpdateAchAuthorization from 'components/FinancialCheckup/UpdatePayments/UpdateAchAuthorization/UpdateAchAuthorization';
import UpdateAchWaitingAuthorization from 'components/FinancialCheckup/UpdatePayments/UpdateAchWaitingAuthorization';
import YourLoans from 'components/FinancialCheckup/FinancialDashboard/Loan/YourLoans';
import LoanHistoryPage from 'components/FinancialCheckup/FinancialDashboard/Loan/LoansHistoryPage';

import { NextFunction, RouterType } from './types';
import { getShiftsRoutes } from './ShiftsRoutes';

const UPDATE_PAYMENTS_TITLE = 'Update payments';

export const getFinancialCheckupRoutes = (): RouterType => ({
  [RoutePath.FinancialDashboard]: {
    component: FinancialDashboard,
    handleNext: () => () => {},
  },
  [RoutePath.YourLoans]: {
    navigationInfo: { showBackLink: true, title: 'Your loans' },
    component: YourLoans,
  },
  [RoutePath.ManageYourLoan]: {
    navigationInfo: { showBackLink: true, title: 'Your loan' },
    component: ManageYourLoan,
  },
  [RoutePath.LoansHistory]: {
    navigationInfo: { showBackLink: true, title: 'Loan history' },
    component: LoanHistoryPage,
  },
  [RoutePath.MethodPayment]: {
    navigationInfo: { showBackLink: true, title: 'Transaction details' },
    component: TransactionDetail,
  },
  ...getShiftsRoutes(),
  [RoutePath.LoanPayment]: {
    navigationInfo: { showBackLink: true, title: 'Payment details' },
    component: LoanPaymentDetail,
  },
  [RoutePath.PaymentSettings]: {
    navigationInfo: { showBackLink: true, title: 'Payment settings' },
    component: PaymentSettings,
  },
  [RoutePath.UpdateEmployer]: {
    navigationInfo: { showBackLink: true, title: UPDATE_PAYMENTS_TITLE },
    component: UpdateEmployer,
    handleNext: ({ navigate, params }) => () => {
      navigate(generatePath(RoutePath.UpdateSetupDeduction, { loanId: params.loanId }) as RoutePath);
    },
  },
  [RoutePath.UpdateSetupDeduction]: {
    navigationInfo: { showBackLink: false, title: UPDATE_PAYMENTS_TITLE },
    component: withFinancialDashboardNavigation(SetupDeduction),
    handleNext: ({ navigate, params }) => (result) => {
      switch (result) {
        case RepaymentDecisionResult.No:
          navigate(generatePath(RoutePath.UpdateNotifyYourTeam, { loanId: params.loanId }) as RoutePath);
          break;
        case RepaymentDecisionResult.Yes:
          navigate(generatePath(RoutePath.UpdatePaystubsSignInGuide, { loanId: params.loanId }) as RoutePath);
          break;
        default:
          navigate(generatePath(RoutePath.PaymentSettings, { loanId: params.loanId }) as RoutePath);
      }
    },
  },
  [RoutePath.UpdatePaystubsSignInGuide]: {
    navigationInfo: { showBackLink: true, title: UPDATE_PAYMENTS_TITLE },
    component: withFinancialDashboardNavigation(PaystubsSignInGuide),
    handleNext: ({ navigate, params }) => (result) => {
      switch (result) {
        case RepaymentDecisionResult.No:
          navigate(generatePath(RoutePath.UpdateNotifyYourTeam, { loanId: params.loanId }) as RoutePath);
          break;
        default:
          navigate(generatePath(RoutePath.UpdateSetupViaPayroll, { loanId: params.loanId }) as RoutePath);
      }
    },
  },
  [RoutePath.UpdateSetupViaPayroll]: {
    navigationInfo: { title: UPDATE_PAYMENTS_TITLE },
    component: withFinancialDashboardNavigation(SetupViaPayroll),
    handleNext: ({ navigate, params }) => (result) => {
      switch (result) {
        case RepaymentDecisionResult.No:
          navigate(generatePath(RoutePath.UpdateWhatIsTheIssue, { loanId: params.loanId }) as RoutePath);
          break;
        default:
          navigate(generatePath(RoutePath.UpdateVerifyPayrollDeduction, { loanId: params.loanId }) as RoutePath);
      }
    },
  },
  [RoutePath.UpdateWhatIsTheIssue]: {
    navigationInfo: { title: UPDATE_PAYMENTS_TITLE },
    component: withFinancialDashboardNavigation(WhatIsTheIssue),
    handleNext: ({ navigate, params }) => (errorOptionSelected) => {
      navigate(generatePath(RoutePath.UpdateNotifyYourTeam, { loanId: params.loanId }) as RoutePath, {
        state: { errorOptionSelected },
      });
    },
  },
  [RoutePath.UpdateNotifyYourTeam]: {
    navigationInfo: { title: 'Notify your team' },
    component: withFinancialDashboardNavigation(NotifyYourTeam),
    handleNext: ({ navigate, params }) => () => {
      navigate(generatePath(RoutePath.UpdateVerifyPayrollDeduction, { loanId: params.loanId }) as RoutePath);
    },
  },
  [RoutePath.UpdateVerifyPayrollDeduction]: {
    navigationInfo: { showBackLink: false, title: UPDATE_PAYMENTS_TITLE },
    component: withFinancialDashboardNavigation(VerifyPayrollDeduction),
    handleNext: ({ navigate, params }) => () => {
      navigate(generatePath(RoutePath.PaymentSettings, { loanId: params.loanId }) as RoutePath);
    },
  },
  [RoutePath.UpdateAchConnect]: {
    navigationInfo: { showBackLink: true, title: UPDATE_PAYMENTS_TITLE },
    component: UpdateAchConnect,
    handleNext: ({ navigate, params }) => (result) => {
      switch (result) {
        case CheckingAccountResult.Failure:
          navigate(RoutePath.Error);
          break;
        default:
          navigate(generatePath(RoutePath.UpdateAchAuthorization, { loanId: params.loanId }) as RoutePath);
          break;
      }
    },
  },
  [RoutePath.UpdateAchAuthorization]: {
    navigationInfo: { showBackLink: true, title: UPDATE_PAYMENTS_TITLE },
    component: UpdateAchAuthorization,
    handleNext: ({ navigate, params }) => () => {
      navigate(generatePath(RoutePath.UpdateAchWaitingAuthorization, { loanId: params.loanId }) as RoutePath);
    },
  },
  [RoutePath.UpdateAchWaitingAuthorization]: {
    component: UpdateAchWaitingAuthorization,
  },
});

export const backToFinancialDashboard: NextFunction = ({ navigate }) => () => {
  navigate(RoutePath.FinancialDashboard);
};

export const FINANCIAL_DASHBOARD_ROUTES = Object.keys(getFinancialCheckupRoutes());
